import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Timer } from "lucide-react";
import { updateField } from "../../../redux/slices/employee-slice";
import ModalWrapper from "../../../Modal";
import { Select, Tag } from "antd";
import AddLeaves from "../../../modals/addLeaves";
import { getRequest } from "../../../service/index";

const TimeOff = () => {
  const dispatch = useDispatch();
  const form = useSelector((state) => state.employeeForm);

  const [leaves, setLeaves] = useState([]);
  const [addNew, setAddNew] = useState(false);
  const [leaveConfigs, setLeaveConfigs] = useState([]);
  const selectedPolicies = form.annual_leave_policy || [];

  // Memoize fetchLeaveConfigs so it doesn't get recreated on every render
  const fetchLeaveConfigs = useCallback(async () => {
    try {
      const response = await getRequest('/api/leaves');
      if (response?.leaveConfigs) {
        setLeaveConfigs(response.leaveConfigs);
      }
    } catch (error) {
      console.error('Error fetching leave configs:', error);
    }
  }, []);

  // Only fetch leave configs once on mount, or when addNew is closed (to refresh after adding)
  useEffect(() => {
    fetchLeaveConfigs();
    // eslint-disable-next-line
  }, [fetchLeaveConfigs]);

  // Update leaves when selectedPolicies or leaveConfigs change
  useEffect(() => {
    if (!selectedPolicies?.length) {
      setLeaves([]);
      return;
    }

    const newLeaves = selectedPolicies.map(policy => {
      const selectedOption = leaveConfigs.find(opt => opt._id === policy);
      return {
        type: selectedOption?.title,
        category: selectedOption?.leaveDuration || "-",
        balance: selectedOption?.numberOfDays || 0,
      };
    });

    setLeaves(newLeaves);
  }, [selectedPolicies, leaveConfigs]);

  const handlePolicyChange = (value) => {
    dispatch(updateField({ name: "annual_leave_policy", value }));

    if (!value?.length) {
      setLeaves([]);
      return;
    }

    const newLeaves = value.map(policy => {
      const selectedOption = leaveConfigs.find(opt => opt._id === policy);
      return {
        type: selectedOption?.title,
        category: selectedOption?.leaveDuration || '-',
        balance: selectedOption?.numberOfDays || 0,
      };
    });
    setLeaves(newLeaves);
  };

  const handleRemove = (index) => {
    const newSelectedPolicies = [...selectedPolicies];
    newSelectedPolicies.splice(index, 1);
    dispatch(updateField({ name: "annual_leave_policy", value: newSelectedPolicies }));
  };

  const leaveOptions = leaveConfigs.map(config => ({
    id: config._id,
    value: config._id,
    label: config.title,
    days: config.numberOfDays,
    duration: config.leaveDuration
  }));

  const closeTimeOff = () => {
    setAddNew(false);
    // Only refetch leave configs after closing the modal (e.g. after adding a new leave)
    fetchLeaveConfigs();
  };

  return (
    <>
      <ModalWrapper
        title="Add New Leave"
        onRequestClose={closeTimeOff}
        open={addNew}
      >
        <AddLeaves onClose={closeTimeOff} />
      </ModalWrapper>
      <div className="flex items-start justify-between bg-white px-4">
        <div className="flex flex-col gap-3 mt-1">
          <Timer className="h-7 w-7 text-active-color stroke-1" />
          <h4 className="font-bold text-md">Leaves</h4>
          <p className="text-gray-400 text-md -mt-2">
            Fill in the employee's time off details.
          </p>
        </div>
        <div className="bg-form-bg w-lg rounded-md px-4 flex flex-col gap-6 py-8 mt-5">
          <div className="space-y-4 grid grid-cols-12 gap-5">
            <div className="flex flex-col gap-1 col-span-12">
              <div className="flex items-center justify-between ">
                <label htmlFor="fname" className="text-md ">
                  Leave Policy
                  <span className="text-red-500 relative -top-0.5 ml-0.5">
                    *
                  </span>
                </label>

                <span
                  className="text-[8px] px-2 py-0.5 bg-primary rounded-sm text-white font-semibold cursor-pointer hover:bg-active-color hover:text-white"
                  onClick={() => setAddNew(true)}
                >
                  Add
                </span>
              </div>
              <Select
                mode="multiple"
                className="w-full"
                placeholder="Select leave policies"
                onChange={handlePolicyChange}
                value={selectedPolicies}
                options={leaveOptions}
                tagRender={(props) => (
                  <Tag color="blue" closable onClose={props.onClose}>
                    {props.label}
                  </Tag>
                )}
              />
            </div>
            <div className="col-span-12">
              <table className="w-full mt-4 rounded-md col-span-12">
                <thead>
                  <tr className="bg-tablehead">
                    <th className="px-2 py-2 text-md font-normal">
                      Title
                    </th>
                    <th className="px-2 py-2 text-md font-normal">
                      Duration
                    </th>
                    <th className="px-2 py-2 text-md font-normal">
                      Days
                    </th>
                    <th className="px-2 py-2 text-md font-normal">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leaves.map((leave, index) => (
                    <tr className="border border-tablehead" key={index}>
                      <td className="px-2 py-2 text-md text-center">{leave.type}</td>
                      <td className="px-2 py-2 text-md text-center">{leave.category}</td>
                      <td className="px-2 py-2 text-md text-center">{leave.balance}</td>
                      <td className="px-2 py-2 text-md text-center">
                        <button
                          className="bg-red-500 ml-4 text-white text-md px-2 py-0.5 rounded hover:bg-red-600"
                          onClick={() => handleRemove(index)}
                        >
                          -
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TimeOff;
